import React from 'react';
import PropTypes from 'prop-types';
import { Column, List, Row } from '@pella/sash-framework';
import { Image } from '../../..';
import { isValidArray } from '../../../utilities/validations';
import { stripTags } from '../../../utilities/functions';

const AuthorBio = ({ data, renderAs, isBranch, className }) => {
	const schemaTitle = data?.expertise?.filter((item) => item.heading.includes('Title'))?.[0]?.subheading || data?.job_title;
	const schemaEducation = data?.expertise?.filter((item) => /education/i.test(item.heading))?.[0]?.subheading;
	const schemaExpertise = data?.expertise?.filter((item) => /expertise/i.test(item.heading))?.[0]?.subheading.split(', ');

	const Heading = renderAs ? renderAs : 'h2';

	return (
		<div className={`authorBio grid gap-y-6 pb-12 pt-6 md:grid-cols-[1fr_2fr] md:gap-x-4 md:gap-y-0 md:pt-12 xl:grid-cols-[1fr_2fr_1fr] ${className || ''}`}>
			<div className='flex items-center md:flex-col'>
				{isValidArray(data.image) && (
					<div className='authorBio-image col-start-1 md:text-center'>
						<Image
							data={data.image[0]}
							className='h-[150px] w-[150px] rounded-full'
							loading='eager'
						/>
					</div>
				)}

				{isValidArray(data.awards) && (
					<Row
						className='authorBio-awards max-w-80 gap-x-1 pl-8 md:mt-4 md:justify-center md:px-2 md:pl-0'
						noGutters
					>
						{data.awards.map((award, index) => (
							<Column
								className='!max-w-4/12'
								key={index}
							>
								<Image data={{ image: award }} />
							</Column>
						))}
					</Row>
				)}
			</div>

			<div className='authorBio-content col-start-1 md:col-start-2'>
				{data.heading && <Heading className='text-h3 mb-1'>{data.heading}</Heading>}
				{data.job_title && <p className='text-subtitle'>{data.job_title}</p>}
				{isValidArray(data.expertise) && (
					<List
						bullets='none'
						className='mt-6 space-y-3 xl:space-y-2'
					>
						{data.expertise.map((item, index) => (
							<li key={index}>
								{item.heading && <strong className='mr-1'>{item.heading}</strong>}
								{item.subheading && <span>{item.subheading}</span>}
							</li>
						))}
					</List>
				)}

				{data.description && (
					<div
						dangerouslySetInnerHTML={{ __html: data.description }}
						className='mt-6'
					/>
				)}
			</div>

			<script
				type='application/ld+json'
				dangerouslySetInnerHTML={{
					__html: `{
					"@context": "https://schema.org",
					"@type": "ProfilePage",
					"mainEntity": {
						"@type": "Person",
						"name": "${data.heading}",
						"url": "${process.env.GATSBY_SITE_URL + data.url}",
						${
							isValidArray(data.image)
								? `"image": {
							"@type": "ImageObject",
							"url": "${data.image[0].image.url}",
							"description": "Portrait of ${data.heading}"
						},`
								: ''
						}
						"description": "${stripTags(data.description)}",
						"jobTitle": "${schemaTitle}"${
							schemaEducation
								? `,
						"alumniOf": [{
							"@type": "Organization",
							"name": "${schemaEducation}"
						}]`
								: ''
						}${
							schemaExpertise
								? `,
						"knowsAbout": [${schemaExpertise.map((item) => `"${item}"`)}]`
								: ''
						},
						"worksFor": {
							"@type": "Organization",
							"name": "${!isBranch ? 'Pella Windows and Doors' : data.branch.formal_name}",
							"url": "${!isBranch ? 'https://www.pella.com/' : process.env.GATSBY_SITE_URL + data.branch.branch_url}"
						}
					},
					"isPartOf": {
						"@type": "CollectionPage",
						"name": "Articles by ${data.heading}",
						"hasPart": [${data.articles.map((item) => JSON.stringify(item))}]
					}
				}`,
				}}
			/>
		</div>
	);
};

AuthorBio.propTypes = {
	data: PropTypes.object,
	className: PropTypes.string,
	renderAs: PropTypes.string,
	isBranch: PropTypes.bool,
};

export default AuthorBio;
